/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { useRetracked } from 'js/lib/retracked';
import user from 'js/lib/user';

import { Button, Dialog, InlineNotification, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import { AnswerCorrectIcon, LockIcon, SuccessIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import {
  getPriceDisplayFromAmount,
  getReadableDateFromTimestamp,
} from 'bundles/coursera-plus-common/utils/generalUtils';
import { saveUpgradeData } from 'bundles/coursera-plus-common/utils/plusMonthlyToAnnualUpgradeUtils';
import useSubscriptionTiersMidCycleUpgradeMutation from 'bundles/coursera-plus-common/utils/useSubscriptionTiersMidCycleUpgradeMutation';
import type SubscriptionsV1 from 'bundles/naptimejs/resources/subscriptions.v1';
import type { ProductPrices } from 'bundles/ownable-products-common/hooks/useGetProductPrices';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { getPrivacyPolicy, getTermsOfUseLink } from 'bundles/payments-common/common/staticLinks';
import { useWallets } from 'bundles/payments-common/data/useWallets';
import { redactEmail } from 'bundles/payments-common/utils/redactEmail';
import BillingCycleType from 'bundles/subscriptions-common/common/BillingCycleType';

import _t from 'i18n!nls/coursera-plus-common';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const ONE_DAY_IN_MILLISECONDS = 86400000;

const styles = {
  dialog: css`
    position: relative;

    .cds-Dialog-dialog {
      max-width: 700px !important;
    }
  `,
  stepWrapper: css`
    list-style: none;
    padding-left: 0;
    overflow: hidden;
  `,
  step: css`
    display: flex;
    margin-bottom: 8px;
  `,
  stepContent: css`
    margin-bottom: 10px;
    margin-top: -2px;
  `,
  stepIcon: css`
    color: blue;
    margin-right: 8px;
    position: relative;

    &::after {
      display: inline-block;
      content: '';
      position: absolute;
      top: 18.5px;
      left: 8px;
      width: 10px;
      height: calc(100% - 9px);
      border-left: 4px solid var(--cds-color-blue-700);
    }
  `,
  lastIcon: css`
    &::after {
      display: none;
    }
  `,
  paymentDetails: css`
    padding: 20px 0;
    margin: 20px auto;
    border: 1px solid lightgray;
  `,
  underline: css`
    text-decoration: underline;
  `,
  paymentDetailsTitle: css`
    padding: 0 20px;
  `,
  secureTextWrapper: css`
    color: red;
    display: flex;
    margin-top: 8px;
    align-items: center;
    font-size: 18px;
    padding: 0 20px;
  `,
  lockIcon: css`
    margin-right: 8px;
    position: relative;

    &::before {
      display: inline-block;
      content: '';
      position: absolute;
      top: -3px;
      right: -6px;
      height: 28px;
      width: 28px;
      background-color: var(--cds-color-green-100);
      border-radius: 50%;
      opacity: 0.4;
    }
  `,
  walletDetailsRow: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 16px;
    padding: 0 20px;
  `,
  todaysTotalRow: css`
    display: flex;
    justify-content: space-between;
    margin-top: 8px;
    padding: 10px 20px;
  `,
  content: css`
    ${breakpoints.down('xs')} {
      padding: 0;
      margin: var(--cds-spacing-200) var(--cds-spacing-400);
    }
  `,
  action: css`
    display: flex;
    gap: 16px;
    padding-top: 16px;
    ${breakpoints.down('xs')} {
      padding: 0;
      margin: var(--cds-spacing-400);
      flex-direction: column;
    }
  `,
  todaysPrice: css`
    text-align: right;
  `,
  grayText: css`
    color: var(--cds-color-grey-600);
  `,
  title: css`
    padding-bottom: 24px;
    margin-top: var(--cds-spacing-200);
    ${breakpoints.down('xs')} {
      padding: 0;
      margin: var(--cds-spacing-200) var(--cds-spacing-400);
    }
  `,
};

type AnnualLegalCopyInputType = {
  annualPriceDisplay: JSX.Element;
  nextBillingDate: string;
};
const AnnualUpgradeLegalCopy = ({ annualPriceDisplay, nextBillingDate }: AnnualLegalCopyInputType) => {
  const youWillBeCharged = (
    <strong>
      <FormattedMessage
        message={_t(
          "You'll be charged {nextBillingDatePrice} (plus applicable taxes) on the next date of your existing billing cycle, {nextBillingDate}, and yearly thereafter until you cancel."
        )}
        nextBillingDatePrice={annualPriceDisplay}
        nextBillingDate={nextBillingDate}
      />
    </strong>
  );

  return (
    <div>
      <p>
        <Typography2 component="p" variant="bodySecondary">
          <FormattedMessage
            message={_t(
              'By clicking "Switch to Coursera Plus annual," you agree to the terms below, {termsOfUseLink}, and {refundPolicyLink}.'
            )}
            termsOfUseLink={getTermsOfUseLink()}
            refundPolicyLink={getPrivacyPolicy()}
          />
        </Typography2>
      </p>
      <p>
        <Typography2 component="p" variant="bodySecondary">
          <FormattedMessage
            message={_t(
              '{youWillBeCharged} To avoid future charges, you can cancel any time in your account settings.'
            )}
            youWillBeCharged={youWillBeCharged}
          />
        </Typography2>
      </p>
    </div>
  );
};
const AnnualUpgradeSteps = ({
  nextBillingDate,
  annualPriceDisplay,
  lastDayCurrentBillingCycle,
}: {
  nextBillingDate: string;
  annualPriceDisplay: JSX.Element;
  lastDayCurrentBillingCycle: string;
}) => {
  return (
    <ul css={styles.stepWrapper}>
      <li css={styles.step}>
        <span css={styles.stepIcon}>
          <SuccessIcon color="interactive" />
        </span>
        <Typography2 component="p">
          <FormattedMessage
            message={_t('{switchToCourseraAnnual} starting today')}
            switchToCourseraAnnual={<strong>{_t('Switch to Coursera Plus annual')}</strong>}
          />
        </Typography2>
      </li>
      <li css={styles.step}>
        <span css={[styles.stepIcon, styles.lastIcon]}>
          <AnswerCorrectIcon color="interactive" />
        </span>
        <Typography2 component="p">
          <FormattedMessage
            message={_t('{payPricePerYear} starting on {nextBillingDate}')}
            payPricePerYear={
              <strong>
                <FormattedMessage
                  message={_t('Pay {courseraPlusAnnualPrice} a year')}
                  courseraPlusAnnualPrice={annualPriceDisplay}
                />
              </strong>
            }
            nextBillingDate={nextBillingDate}
          />
        </Typography2>
      </li>
    </ul>
  );
};

export type PropsToComponent = {
  subscription: SubscriptionsV1;
  courseraPlusAnnualPrice?: ProductPrices;
  onClose: () => void;
  isOpen: boolean;
  onUpgradeSuccess?: () => void;
};

const AnnualUpgradeModal = ({
  subscription,
  courseraPlusAnnualPrice,
  onClose,
  isOpen,
  onUpgradeSuccess,
}: PropsToComponent) => {
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [ctaLoading, setCtaLoading] = useState(false);
  const { midCycleUpgrade } = useSubscriptionTiersMidCycleUpgradeMutation();
  const retrackedTrack = useRetracked();
  const wallets = useWallets(undefined, true);
  const courseraPlusSubscription = subscription;
  const { braintreeWallets, paypalWallets } = wallets;
  const { Content, HeadingGroup, Actions } = Dialog;

  const nextBillingDate = getReadableDateFromTimestamp(courseraPlusSubscription.nextBillingAt);
  const lastDayCurrentBillingCycle = getReadableDateFromTimestamp(
    courseraPlusSubscription.nextBillingAt - ONE_DAY_IN_MILLISECONDS
  );
  const braintreeWallet = braintreeWallets?.[0];
  const paypalWallet = paypalWallets?.[0];
  if (!courseraPlusSubscription?.nextBillingAt || !courseraPlusAnnualPrice) {
    return null;
  }

  const annualPriceDisplay = getPriceDisplayFromAmount(
    courseraPlusAnnualPrice.amount,
    courseraPlusAnnualPrice.currencyCode
  );
  let lastFourCreditCardDigits;
  if (braintreeWallet?.creditCardLastFourDigits) {
    lastFourCreditCardDigits = (
      <Typography2 component="p" css={styles.grayText}>
        {braintreeWallet?.creditCardType}
        <span style={{ marginLeft: '12px' }}>*</span>
        {braintreeWallet?.creditCardLastFourDigits}
      </Typography2>
    );
  } else if (paypalWallet?.email) {
    lastFourCreditCardDigits = (
      <Typography2 component="p" css={styles.grayText}>
        <FormattedMessage message={_t('PayPal: {paypalEmail}')} paypalEmail={redactEmail(paypalWallet?.email)} />
      </Typography2>
    );
  }

  const handleUpgrade = () => {
    setCtaLoading(true);

    const options = {
      toTierBillingCycle: BillingCycleType.ANNUAL,
      fromTierSubscriptionId: courseraPlusSubscription.id,
    };
    midCycleUpgrade(options)
      .then(() => {
        saveUpgradeData({
          userId: user.get().id,
          subscriptionId: courseraPlusSubscription.id,
          nextBillingDate,
        });
        onUpgradeSuccess?.();
      })
      .catch(() => {
        setCtaLoading(false);
        setShowErrorMessage(true);
      });
  };

  const handleClose = () => {
    onClose();
    retrackedTrack({
      trackingData: { courseraPlusSubscription },
      trackingName: 'close_annual_upgrade_modal',
      action: 'click',
    });
  };
  return (
    <Dialog
      aria-describedby="dialog-content"
      open={isOpen}
      variant="standard"
      width="medium"
      onClose={handleClose}
      css={styles.dialog}
      data-testid="annual-upgrade-modal"
    >
      <HeadingGroup css={styles.title}>{_t('What to expect when you switch')}</HeadingGroup>
      <Content id="dialog-content" css={styles.content}>
        <TrackedDiv
          trackingName="annual_upgrade_modal"
          data-testid="annual-upgrade-modal"
          trackClicks={true}
          withVisibilityTracking={true}
          data={{ courseraPlusSubscription }}
        >
          <AnnualUpgradeSteps
            nextBillingDate={nextBillingDate}
            lastDayCurrentBillingCycle={lastDayCurrentBillingCycle}
            annualPriceDisplay={annualPriceDisplay}
          />
          <div css={styles.paymentDetails}>
            <Typography variant="h2semibold" css={styles.paymentDetailsTitle}>
              {_t('Payment Details')}
            </Typography>
            <div css={styles.secureTextWrapper}>
              <span css={styles.lockIcon}>
                <LockIcon size="small" color="success" />
              </span>
              <Typography2 component="p" css={styles.grayText}>
                {_t('Your payments are processed securely')}
              </Typography2>
            </div>
            {lastFourCreditCardDigits && (
              <div css={styles.walletDetailsRow}>
                <Typography2 component="p">{_t('Pay with')}</Typography2>
                <Typography2 component="p">{lastFourCreditCardDigits}</Typography2>
              </div>
            )}
            <div css={styles.todaysTotalRow}>
              <Typography2 component="h3" variant="subtitleMedium">
                {_t("Today's total: ")}
              </Typography2>
              <div css={styles.todaysPrice}>
                <Typography2 variant="subtitleMedium" component="span">
                  {getPriceDisplayFromAmount(0, courseraPlusAnnualPrice.currencyCode)}
                </Typography2>
                <Typography2 component="p" css={styles.grayText}>
                  <FormattedMessage
                    message={_t('then {annualPriceDisplay}/year starting on {nextBillingDate}')}
                    nextBillingDate={nextBillingDate}
                    annualPriceDisplay={annualPriceDisplay}
                  />
                </Typography2>
              </div>
            </div>
          </div>
          <div data-testid="legal-copy">
            <AnnualUpgradeLegalCopy annualPriceDisplay={annualPriceDisplay} nextBillingDate={nextBillingDate} />
          </div>
        </TrackedDiv>
        {showErrorMessage && (
          <InlineNotification
            severity="error"
            onDismiss={() => {
              setShowErrorMessage(false);
            }}
          >
            {_t('An internal error occurred. Please try again.')}
          </InlineNotification>
        )}
      </Content>
      <Actions css={styles.action}>
        <TrackedButton
          onClick={handleUpgrade}
          loading={ctaLoading}
          disabled={ctaLoading}
          variant="primary"
          size="medium"
          data-testid="annual-upgrade-button"
          trackingName="annual_upgrade_button"
          trackingData={{ subscriptionId: courseraPlusSubscription.id }}
        >
          {_t('Switch to Coursera Plus annual')}
        </TrackedButton>
      </Actions>
    </Dialog>
  );
};

export default AnnualUpgradeModal;
